import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import TodayIcon from '@mui/icons-material/Today';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import { AdminEditableServiceName, AdminServiceStatus, AdminServiceStatusUpdate } from '../../../hooks/useSubscriberAdminDetails';
import { draftToPaidDate, paidDateToDraft } from '../../../utils/servicePaidDate';
import LargeMaterialUISwitch from '../../common/LargeMaterialUISwitch';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';

type Props = {
    title: string;
    description?: string;
    service: AdminEditableServiceName;
    /** Current (server) status. undefined = the service was never configured for this subscriber */
    status?: AdminServiceStatus;
    isLoading?: boolean;
    saving?: boolean;
    /**
     * Called with only the fields that were touched (active and/or paidDate,
     * paidDate null = clear the date). Return true on a successful save.
     */
    onSave: (service: AdminEditableServiceName, update: AdminServiceStatusUpdate) => Promise<boolean>;
};

/**
 * Summarises the payment state for a paid date using the same rule as
 * isSextforceActive: a payment covers one month plus one day of grace
 */
const paymentStatus = (
    paidDate: moment.Moment | null,
): { label: string; color: 'success.main' | 'error.main' | 'text.secondary'; icon: ReactNode } => {
    if (!paidDate) {
        return {
            label: 'No payment on record',
            color: 'text.secondary',
            icon: <RemoveCircleOutlineIcon fontSize="inherit" />,
        };
    }

    const paidUntil = paidDate.clone().add(1, 'months').add(1, 'days').endOf('day');

    if (paidUntil.isSameOrAfter(moment())) {
        const daysLeft = Math.max(0, paidUntil.diff(moment(), 'days'));

        return {
            label: `Paid until ${paidUntil.format('D MMM YYYY')} · ${daysLeft === 0 ? 'last day' : `${daysLeft} days left`}`,
            color: 'success.main',
            icon: <CheckCircleIcon fontSize="inherit" />,
        };
    }

    return {
        label: `Payment lapsed ${paidUntil.fromNow()}`,
        color: 'error.main',
        icon: <ErrorIcon fontSize="inherit" />,
    };
};

/**
 * Standalone editor for a single service's active status and paid date.
 * Used on the Admin Subscriber Edit page and reusable on the individual
 * services' dashboard pages.
 */
const AdminServiceStatusCard = (props: Props) => {
    const { title, description, service, status, isLoading, saving, onSave } = props;

    const baselineActive: boolean = status?.active === true;
    const baselinePaidDate: moment.Moment | null = paidDateToDraft(status?.paidDate);

    const [draftActive, setDraftActive] = useState<boolean>(baselineActive);
    const [draftPaidDate, setDraftPaidDate] = useState<moment.Moment | null>(baselinePaidDate);
    const [internalSaving, setInternalSaving] = useState<boolean>(false);

    // Re-hydrate the draft whenever fresh server values arrive
    const statusFingerprint = JSON.stringify({ active: status?.active ?? null, paidDate: status?.paidDate ?? null });

    useEffect(() => {
        setDraftActive(status?.active === true);
        setDraftPaidDate(paidDateToDraft(status?.paidDate));
    }, [statusFingerprint]);

    const activeTouched: boolean = draftActive !== baselineActive;
    const paidDateTouched: boolean =
        (draftPaidDate === null) !== (baselinePaidDate === null) ||
        (draftPaidDate !== null && baselinePaidDate !== null && !draftPaidDate.isSame(baselinePaidDate, 'day'));

    const isDirty = activeTouched || paidDateTouched;
    const isSaving = internalSaving || saving === true;

    const payment = paymentStatus(draftPaidDate);

    const handleReset = () => {
        setDraftActive(baselineActive);
        setDraftPaidDate(baselinePaidDate);
    };

    const handleSave = async () => {
        if (!isDirty || isSaving) {
            return;
        }

        const update: AdminServiceStatusUpdate = {};

        if (activeTouched) {
            update.active = draftActive;
        }

        if (paidDateTouched) {
            update.paidDate = draftToPaidDate(draftPaidDate);
        }

        setInternalSaving(true);

        await onSave(service, update).finally(() => {
            setInternalSaving(false);
        });
    };

    return (
        <Card variant="elevation" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <StyledCardTitleBar
                title={title}
                badge={
                    isDirty ? (
                        <Chip label="Unsaved" size="small" color="warning" sx={{ fontWeight: 'bold' }} />
                    ) : (
                        <Chip
                            label={baselineActive ? 'Active' : 'Off'}
                            size="small"
                            color={baselineActive ? 'success' : 'default'}
                            sx={{ fontWeight: 'bold' }}
                        />
                    )
                }
                isLoading={isLoading}
            />
            <CardContent sx={{ flexGrow: 1, paddingBottom: 0 }}>
                {isLoading ? (
                    <Stack spacing={2}>
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="rounded" height={51} />
                        <Skeleton variant="rounded" height={40} />
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        {description && (
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        )}

                        <Divider />

                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Service Active
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {draftActive ? 'Runs for this subscriber' : 'Switched off for this subscriber'}
                                </Typography>
                            </Box>
                            <LargeMaterialUISwitch checked={draftActive} onChange={(_e, checked) => setDraftActive(checked)} />
                        </Stack>

                        <Divider />

                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DesktopDatePicker
                                        label="Date Paid"
                                        inputFormat="DD/MM/YYYY"
                                        showDaysOutsideCurrentMonth={true}
                                        value={draftPaidDate}
                                        onChange={(value: moment.Moment | null) => setDraftPaidDate(value)}
                                        renderInput={params => <TextField {...params} size="small" fullWidth />}
                                    />
                                </LocalizationProvider>
                                <Tooltip title="Set to today's date (GMT)">
                                    <span>
                                        <IconButton size="small" onClick={() => setDraftPaidDate(moment.utc().startOf('day'))}>
                                            <TodayIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Add one month to Date Paid">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setDraftPaidDate(
                                                    (draftPaidDate ?? moment.utc().startOf('day')).clone().add(1, 'months'),
                                                )
                                            }
                                        >
                                            <EventRepeatIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Clear Date Paid">
                                    <span>
                                        <IconButton size="small" disabled={draftPaidDate === null} onClick={() => setDraftPaidDate(null)}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{ marginTop: 1, fontSize: 16, color: payment.color }}
                            >
                                {payment.icon}
                                <Typography variant="caption" sx={{ color: 'inherit' }}>
                                    {payment.label}
                                </Typography>
                            </Stack>
                        </Box>

                        {!status && (
                            <Typography variant="caption" color="text.secondary">
                                This service is not configured for this subscriber yet. Saving will set it up.
                            </Typography>
                        )}
                    </Stack>
                )}
            </CardContent>
            <CardActions
                sx={{
                    justifyContent: 'flex-end',
                    paddingX: 2,
                    paddingY: 1.5,
                    marginTop: 2,
                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button size="small" startIcon={<RestartAltIcon />} disabled={!isDirty || isSaving || isLoading} onClick={handleReset}>
                    Reset
                </Button>
                <LoadingButton
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    loading={isSaving}
                    disabled={!isDirty || isLoading}
                    onClick={handleSave}
                >
                    Save
                </LoadingButton>
            </CardActions>
        </Card>
    );
};

export default AdminServiceStatusCard;
